import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { hudPaths, toHudHash } from '../content';
import { hudResourceDocumentByPath, hudResourceDocuments, HudResourceDocument, HudResourcePath } from '../resourceContent';
import { HudPanel, MetricBar, ScannerOverlay, SystemButton, SystemFrame } from '../primitives';
import { PageActionBar } from './PageActionBar';

const ResourceDocumentScreen = ({ document }: { document: HudResourceDocument }) => {
  const reducedMotion = useReducedMotion();
  const relatedDocuments = hudResourceDocuments.filter((item) => item.path !== document.path);

  return (
    <SystemFrame id={document.path.replace(/\//g, '-')} className="min-h-[calc(100vh-6.5rem)] pb-4 pt-24">
      <section className="relative isolate overflow-hidden rounded-[1.4rem] border border-brand-lime/25 bg-[radial-gradient(circle_at_42%_22%,rgba(13,30,36,0.42),rgba(2,7,11,0.97)_72%)] p-3 sm:p-5">
        <ScannerOverlay tone="normal" className="opacity-85" />
        <div className="pointer-events-none absolute inset-0 hud-grid opacity-22" />
        <motion.div
          className="pointer-events-none absolute inset-y-0 left-[-30%] w-[22%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.16),transparent)]"
          animate={reducedMotion ? undefined : { x: ['0%', '560%'], opacity: [0, 0.9, 0] }}
          transition={{ duration: 4.8, repeat: Infinity, ease: 'linear', repeatDelay: 1.2 }}
        />

        <div className="relative z-20">
          <HudPanel pulse className="bg-brand-panel/74">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="hud-micro text-brand-aqua">{document.id} // RESOURCE COMMAND NODE</p>
                <h1 className="mt-2 font-display text-2xl text-brand-text sm:text-[2rem]">{document.title}</h1>
                <p className="mt-2 max-w-4xl text-sm text-brand-text-2 sm:text-base">{document.subtitle}</p>
              </div>
              <div className="flex flex-col items-start gap-2 sm:items-end">
                <span className="hud-chip border-brand-aqua/45 bg-brand-aqua/10 text-brand-aqua">SOURCE VERIFIED</span>
                {document.effectiveDate ? (
                  <span className="hud-micro rounded border border-brand-lime/30 bg-brand-panel-2/80 px-2 py-1 text-brand-lime">
                    LAST UPDATED: {document.effectiveDate}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-brand-lime/20 pt-3">
              <span className="hud-micro rounded border border-brand-lime/30 bg-brand-panel-2/70 px-2 py-1 text-brand-lime">
                CANONICAL SOURCE
              </span>
              <a
                href={document.sourceUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="font-mono text-xs uppercase tracking-[0.18em] text-brand-aqua transition hover:text-brand-text"
              >
                {document.sourceUrl}
              </a>
            </div>
          </HudPanel>

          <div className="mt-4 grid gap-4 2xl:grid-cols-[14.5rem_minmax(0,1fr)_16rem]">
            <HudPanel pulse className="space-y-4 bg-brand-panel-2/70">
              <p className="hud-micro text-brand-lime">Legal Telemetry</p>
              <div className="space-y-3">
                {document.telemetry.map((metric) => (
                  <MetricBar key={metric.label} label={metric.label} value={metric.value} />
                ))}
              </div>
              <div className="border-t border-brand-lime/18 pt-3">
                <p className="hud-micro text-brand-text-3">SECTION COUNT</p>
                <p className="mt-2 font-display text-3 text-brand-lime">{document.sections.length.toString().padStart(2, '0')}</p>
              </div>
            </HudPanel>

            <HudPanel pulse className="bg-brand-panel-2/68">
              <p className="hud-micro text-brand-lime">Policy Feed</p>
              <div className="mt-3 space-y-3">
                {document.sections.map((section, index) => (
                  <article key={section.title} className="relative overflow-hidden rounded-lg border border-brand-lime/18 bg-brand-panel/70 px-3 py-3">
                    <motion.div
                      className="pointer-events-none absolute inset-y-0 left-0 w-px bg-brand-lime/45"
                      animate={reducedMotion ? undefined : { opacity: [0.35, 1, 0.35] }}
                      transition={{ duration: 1.9, repeat: Infinity, delay: index * 0.08 }}
                    />
                    <p className="hud-micro text-brand-aqua">SECTION {(index + 1).toString().padStart(2, '0')}</p>
                    <h2 className="mt-1 text-sm font-semibold uppercase tracking-[0.13em] text-brand-text sm:text-[0.92rem]">{section.title}</h2>
                    <div className="mt-2 space-y-2">
                      {section.body.map((paragraph) => (
                        <p key={paragraph} className="text-sm leading-relaxed text-brand-text-2">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    {section.bullets ? (
                      <ul className="mt-2 grid gap-1.5">
                        {section.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-2 text-sm text-brand-text-2">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-lime" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </article>
                ))}
              </div>
            </HudPanel>

            <HudPanel pulse className="space-y-3 bg-brand-panel-2/72">
              <p className="hud-micro text-brand-aqua">Resource Launch Controls</p>
              {relatedDocuments.map((item) => (
                <SystemButton key={item.path} href={toHudHash(item.path)} variant="secondary" className="w-full justify-center px-3 py-2">
                  Open {item.title}
                </SystemButton>
              ))}
              <div className="mt-2 border-t border-brand-lime/20 pt-3">
                <SystemButton href={toHudHash(hudPaths.home)} className="w-full justify-center px-3 py-2">
                  Return to Start Panel
                </SystemButton>
              </div>
            </HudPanel>
          </div>
        </div>
      </section>
    </SystemFrame>
  );
};

const ResourcePage = ({ path, ctaLabel }: { path: HudResourcePath; ctaLabel: string }) => {
  const document = hudResourceDocumentByPath[path];
  return (
    <>
      <ResourceDocumentScreen document={document} />
      <PageActionBar activePath={path} ctaLabel={ctaLabel} />
    </>
  );
};

export const TermsOfServicePage = () => {
  return <ResourcePage path={hudPaths.termsOfService} ctaLabel="Request Terms Clarification" />;
};

export const PrivacyPolicyPage = () => {
  return <ResourcePage path={hudPaths.privacyPolicy} ctaLabel="Request Data Handling Clarification" />;
};

export const CookiePolicyPage = () => {
  return <ResourcePage path={hudPaths.cookiePolicy} ctaLabel="Request Cookie Control Guidance" />;
};

export const PromotionTermsPage = () => {
  return <ResourcePage path={hudPaths.promotionTerms} ctaLabel="Request Promotion Validation" />;
};
